/**
 * Vorlage „Würzen und abschmecken“.
 *
 * Über dem Topf wird Salz gestreut, Pfeffer gemahlen oder es werden Kräuter
 * hineingegeben. Danach rührt ein Kochlöffel das Gewürz unter.
 */
import { ease, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { circle, compact, faded, group, line, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { falling, motionLines, surfaceLine } from '../parts/effects.ts';
import { saltShaker } from '../parts/kitchen.ts';
import { arcArrow, counter } from '../parts/stage.ts';
import { GRATE_Y, stove } from '../parts/stove.ts';
import { place, woodenSpoon } from '../parts/utensils.ts';
import { contents, pot } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const POT = { cx: 100, width: 116, height: 48 } as const;
const RIM_Y = GRATE_Y - POT.height;
const LEVEL = 0.26;
/** Lage des Streuers bzw. der Mühle über dem Topf. */
const SPICE = { x: POT.cx + 8, y: 34 } as const;

/** Gewürze und wie sie fallen. */
const SPICES = { salt: 'Salz', pepper: 'Pfeffer', herbs: 'Kräuter' } as const;
type Spice = keyof typeof SPICES;
const GRAINS: Readonly<Record<Spice, { tone: Tone; size: number; count: number }>> = {
  salt: { tone: 'deep', size: 1.8, count: 14 },
  pepper: { tone: 'ink', size: 1.7, count: 12 },
  herbs: { tone: 'deep', size: 2.6, count: 9 },
};

/** Zeitplan. */
const TIME = {
  appear: [0.02, 0.1],
  spice: [0.1, 0.56],
  vanish: [0.56, 0.62],
  stir: [0.62, 0.9],
} as const;

/** Pfeffermühle, Mitte unten im Ursprung; der Kopf dreht sich mit turn. */
function pepperMill(turn: number): SceneNode[] {
  const stripes: SceneNode[] = [0, 1, 2].map((index) => {
    const x = -7 + ((index * 5 + turn * 14) % 14);
    return line(x, -56, x, -46, styles.fine('deep'));
  });
  const head = roundedRectPath(-9, -58, 18, 14, 5);
  return [
    shape(path().move(-5, 0).line(5, 0).line(7, -8).line(-7, -8).close().toString(), styles.solid('ink')),
    shape(roundedRectPath(-8, -44, 16, 37, 6), styles.solid('tint')),
    shape(head, styles.flat('shade')),
    group(stripes, { clip: head }),
    shape(head, styles.outline()),
    circle(0, -61, 3, styles.solid('ink')),
  ];
}

/** Hand voll Kräuter: kleine Blätter zwischen Daumen und Finger angedeutet. */
function herbBunch(): SceneNode[] {
  return [
    shape(
      path().move(-10, -4).quad(-14, -16, -4, -22).quad(-2, -12, -10, -4).close().toString(),
      styles.solid('deep'),
    ),
    shape(
      path().move(2, -6).quad(4, -20, 14, -22).quad(12, -10, 2, -6).close().toString(),
      styles.solid('deep'),
    ),
    shape(path().move(-4, -2).quad(-2, -16, 4, -26).toString(), {
      stroke: 'ink',
      strokeWidth: STROKE.detail,
    }),
  ];
}

/** Vorlage „Würzen und abschmecken“. */
export const prepSeasonTemplate = defineTemplate({
  id: 'prep.season',
  group: 'prep',
  title: 'Würzen und abschmecken',
  description: 'Salz, Pfeffer oder Kräuter kommen über dem Topf hinein und werden anschließend untergerührt.',
  durationSeconds: 5,
  keywords: [
    'würzen',
    'abschmecken',
    'nachwürzen',
    'salzen',
    'pfeffern',
    'salz und pfeffer',
    'mit salz',
    'mit pfeffer',
    'kräuter unterrühren',
  ],
  stillTime: 0.32,
  parameters: [choice('spice', 'Gewürz', SPICES, 'salt')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/kräuter|petersilie|schnittlauch|basilikum|dill/.test(lower)) return { spice: 'herbs' };
    if (lower.includes('pfeffer') && !lower.includes('salz')) return { spice: 'pepper' };
    if (lower.includes('salz')) return { spice: 'salt' };
    return {};
  },
  scene(values, t) {
    const spice = textValue(values, 'spice', 'salt') as Spice;
    const grains = GRAINS[spice] ?? GRAINS.salt;
    const vessel = pot(POT.cx, RIM_Y, POT.width, POT.height);
    const burner = stove(POT.cx, 0.3, t);
    const surfaceY = RIM_Y + vessel.depth * LEVEL;
    const rx = vessel.widthAt(LEVEL);
    const ry = vessel.ry * (rx / vessel.rx);

    const tool = ease.outCubic(segment(t, ...TIME.appear)) * (1 - segment(t, ...TIME.vanish));
    const spicePhase = segment(t, ...TIME.spice);
    const active = spicePhase > 0 && spicePhase < 1;
    const stirPhase = segment(t, ...TIME.stir);
    const spoon =
      segment(t, TIME.stir[0], TIME.stir[0] + 0.04) * (1 - segment(t, TIME.stir[1] - 0.04, TIME.stir[1]));

    // Das Gewürz fällt nur während des Streuens; schleifenfest über ganze Umläufe.
    const fall = active
      ? falling(SPICE.x, SPICE.y + 14, surfaceY, 18, grains.count, t * 2, {
          seed: 5,
          size: grains.size,
          loops: 3,
          tone: grains.tone,
        })
      : [];
    // Auf der Oberfläche bleiben ein paar Körner liegen, bis umgerührt ist.
    const settled = Math.min(1, spicePhase * 1.5) * (1 - stirPhase);
    const specks: SceneNode[] = [-26, -12, 4, 18, 30].map((dx, index) =>
      circle(POT.cx + dx, surfaceY + (index % 2 === 0 ? -2 : 3), grains.size, styles.flat(grains.tone)),
    );
    const liquid = contents(vessel, LEVEL, 'shade', [
      shape(surfaceLine(POT.cx - rx, surfaceY - ry * 0.2, rx * 2, 0.8, t, 2), {
        stroke: 'deep',
        strokeWidth: STROKE.fine,
      }),
      faded(settled, specks),
    ]);

    let utensil: SceneNode | null = null;
    if (spice === 'salt') {
      utensil = group([saltShaker(SPICE.x, SPICE.y, 150 + (active ? 16 * wave(t, 12) : 0))], {
        transform: { scale: 1.4, ox: SPICE.x, oy: SPICE.y },
      });
    } else if (spice === 'pepper') {
      // Mahlwerk unten, der Kopf dreht sich.
      utensil = group(
        compact([
          place(SPICE.x, SPICE.y + 14, 0, pepperMill(active ? spicePhase * 6 : 0), 0.7),
          active ? motionLines(SPICE.x + 12, SPICE.y - 22, -20, 7, 2, 5) : null,
        ]),
      );
    } else {
      utensil = place(SPICE.x, SPICE.y + 14 + (active ? 3 * wave(t, 8) : 0), 0, herbBunch());
    }

    const stirAngle = stirPhase * Math.PI * 4;
    return compact([
      ...counter(),
      ...burner.back,
      ...vessel.back,
      liquid,
      faded(spoon, [
        place(
          POT.cx + Math.cos(stirAngle) * 28,
          surfaceY + 4 + Math.sin(stirAngle) * 4,
          18,
          woodenSpoon(),
          0.75,
        ),
      ]),
      ...vessel.front,
      ...burner.front,
      ...fall,
      faded(tool, [utensil]),
    ]);
  },
  hints() {
    return [arcArrow(POT.cx, RIM_Y + 4, 38, 9, 200, 340)];
  },
});
